import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../Icon'
import ContactForm from '../ContactForm'
import { APP_LINKS, CONTACT } from '../../siteConfig'
import { animateCounter, prefersReducedMotion } from '../../hooks/useLandingEffects'
import ratesWatchlist from '../../assets/images/screens/rates-watchlist.webp'
import ratesPriceTrend from '../../assets/images/screens/rates-price-trend.webp'
import farmSelector from '../../assets/images/screens/farm-selector.webp'
import farmLogTransaction from '../../assets/images/screens/farm-log-transaction.webp'
import marketFeeds from '../../assets/images/screens/market-feeds.webp'
import marketChicks from '../../assets/images/screens/market-chicks.webp'
import flockAddFlock from '../../assets/images/screens/flock-add-flock.webp'
import flockDailyData from '../../assets/images/screens/flock-daily-data.webp'
import vetScheduleVisit from '../../assets/images/screens/vet-schedule-visit.webp'
import vetVisitReviews from '../../assets/images/screens/vet-visit-reviews.webp'
import supportChat from '../../assets/images/screens/support-chat.webp'

/* ---------- live rate ticker ---------- */

export const Ticker = ({ rates }) => {
    const items = rates.data || []
    if (!items.length) return null
    return (
        <div className="ticker" data-anim aria-label="Latest poultry rates">
            <div className="ticker-track">
                {/* rendered twice so the -50% loop is seamless */}
                {[...items, ...items].map((r, i) => (
                    <span className="ticker-item" key={i} aria-hidden={i >= items.length}>
                        <span className="loc">{r.city} · {r.type}</span><b>{r.price}</b>
                        <span className={r.up ? 'up' : 'down'}>{r.flat ? '–' : r.up ? '▲' : '▼'} {r.change}</span>
                    </span>
                ))}
            </div>
        </div>
    )
}

/* ---------- stats strip ---------- */

// Shown if the stats API fails.
const FALLBACK_STATS = { farmers: 12400, states: 18, farms: 3200, vets: 150 }

// Live counts are rounded down to a step and shown with "+" (12,437 -> 12,400+).
const STATS = [
    { key: 'farmers', step: 100, label: 'Registered Farmers' },
    { key: 'states', label: 'States Covered' },
    { key: 'farms', step: 100, label: 'Farms Managed' },
    { key: 'vets', step: 10, label: 'Partner Vets' },
]

const roundDown = (value, step) => {
    if (!step || value < step) return { target: value, suffix: '' }
    return { target: Math.floor(value / step) * step, suffix: '+' }
}

// Counts up when scrolled into view, and again if the target changes after
// live numbers arrive.
const CountUp = ({ target, suffix }) => {
    const ref = useRef(null)
    useEffect(() => {
        const el = ref.current
        if (!el) return undefined
        if (prefersReducedMotion()) {
            el.textContent = target.toLocaleString('en-IN') + suffix
            return undefined
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                animateCounter(el)
                observer.disconnect()
            }
        }, { threshold: 0.5 })
        observer.observe(el)
        return () => observer.disconnect()
    }, [target, suffix])
    return <span ref={ref} data-target={target} data-suffix={suffix}>0</span>
}

export const StatsStrip = ({ stats }) => {
    const values = stats.data || (stats.error ? FALLBACK_STATS : null)
    return (
        <section className="stats-strip">
            <div className="wrap stats-row reveal-stagger">
                {STATS.map((s) => {
                    const { target, suffix } = values ? roundDown(Number(values[s.key]) || 0, s.step) : {}
                    return (
                        <div className="stat" key={s.key}>
                            <div className="num">{values ? <CountUp target={target} suffix={suffix} /> : '—'}</div>
                            <div className="label">{s.label}</div>
                        </div>
                    )
                })}
                <div className="stat">
                    <div className="num">24/7</div>
                    <div className="label">Support Availability</div>
                </div>
            </div>
        </section>
    )
}

/* ---------- module grid ---------- */

const MODULES = [
    { icon: 'trendingUp', title: 'Live Poultry Rates', text: 'Track daily egg, broiler, chick and hatching egg rates across India, updated every day.' },
    { icon: 'house', title: 'Farm & Flock Management', text: 'Add multiple farms, track flocks by hatch date, breed and size, and manage customers.' },
    { icon: 'stethoscope', title: 'Vet Doctor Booking', text: 'Schedule a poultry vet visit, get routine checkups, and keep flocks healthy.' },
    { icon: 'cart', title: 'Poultry Marketplace', text: 'Buy or sell feed, equipment, eggs and chicks directly with suppliers and vendors.' },
    { icon: 'chart', title: 'Reports & Records', text: 'Generate flock and farm reports covering feed, mortality, and productivity trends.' },
    { icon: 'message', title: 'Expert Chat Support', text: 'Message our support and vet team directly, share images, and resolve issues fast.' },
]

export const Modules = () => (
    <section className="section" id="features">
        <div className="wrap">
            <div className="head-block reveal">
                <span className="eyebrow">One App, Every Poultry Need</span>
                <h2>Everything your farm needs, in one place</h2>
                <p className="sub">From daily rates to vet visits, Poultryfy replaces scattered calls, notebooks and messages with one connected app.</p>
            </div>
            {/* the Features nav link lands here, so the cards lead */}
            <div className="module-grid reveal-stagger" data-nav-anchor>
                {MODULES.map((m) => (
                    <div className="module-card tilt" key={m.title}>
                        <div className="egg-icon"><Icon name={m.icon} /></div>
                        <h4>{m.title}</h4>
                        <p>{m.text}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
)

/* ---------- impact numbers (placeholder figures) ---------- */

const IMPACT = [
    { prefix: 'Up to ', target: 40, suffix: '%', text: 'Less time spent tracking rates manually' },
    { prefix: 'Up to ', target: 35, suffix: '%', text: 'Reduction in flock record-keeping errors' },
    { target: 3, suffix: 'x', text: 'Faster vet appointment booking' },
    { target: 92, suffix: '%', text: 'Farmer satisfaction with support response' },
]

export const Impact = () => (
    <section className="section section-cream">
        <div className="egg-bg a" /><div className="egg-bg b" />
        <div className="wrap">
            <div className="head-block reveal">
                <span className="eyebrow">Measurable Impact</span>
                <h2>Built to save time and reduce losses on your farm</h2>
            </div>
            <div className="impact-grid reveal-stagger">
                {IMPACT.map((item) => (
                    <div className="impact-card tilt" key={item.text}>
                        <div className="big">{item.prefix}<span className="counter" data-target={item.target} data-suffix={item.suffix}>0</span></div>
                        <div className="desc">{item.text}</div>
                    </div>
                ))}
            </div>
        </div>
    </section>
)

/* ---------- feature rows ---------- */

const FEATURES = [
    {
        eyebrow: 'Live Rates', screens: [
            [ratesWatchlist, 'Poultryfy home screen with a watchlist of egg rates by city'],
            [ratesPriceTrend, 'Price trend chart for egg rates in Raipur'],
        ],
        title: 'Check Latest Egg, Broiler, Chicks and Hatching Egg Rates All Over India',
        points: [
            'Track Egg, Broiler, Chicks and Hatching Egg rates in your city and all over India.',
            'Get daily notifications and rate updates totally free of cost.',
            'Check current date price updates and compare against past rates.',
        ],
    },
    {
        eyebrow: 'Farm & Customer Management', screens: [
            [farmSelector, 'Farm selector listing multiple poultry farms'],
            [farmLogTransaction, 'Log Transaction form for recording a sale to a customer'],
        ],
        title: 'Create and Manage Poultry Farms and Customers',
        points: [
            'Add multiple Poultry Farms, Farm Managers, and Customers, and create Sale Records.',
            'Easily open a Broiler, Egg, Chicks or Hatch Egg Farm and assign Farm Managers.',
            'Different unique features ease the problems of poultry farmers and customers.',
            'Maintain records in minimum clicks and within minutes.',
        ],
    },
    {
        eyebrow: 'Marketplace', screens: [
            [marketFeeds, 'Marketplace listing poultry feeds with Place Order buttons'],
            [marketChicks, 'Marketplace listing chicks from hatcheries'],
        ],
        title: 'Buy, Sell and Enquire for All Poultry Related Products',
        points: [
            'Search and buy Poultry Feed, Equipment, Eggs, Chicks and related products from top suppliers.',
            'Get your own products listed and start selling on Poultryfy.',
            'Inquire about products and talk to our expert team for the best prices.',
        ],
    },
    {
        eyebrow: 'Flock Management', screens: [
            [flockAddFlock, 'Add Flock form with farm, flock size and breed'],
            [flockDailyData, 'Add Daily Data screen with feed consumption and alerts'],
        ],
        title: 'Flock Management',
        points: [
            'Create Flock Reports and manage your flock as per the hatching date.',
            'Record mortality rate, production rate, total feed required and body weight.',
            'Get insights on Brooding and Growing to properly manage your flock.',
        ],
    },
    {
        eyebrow: 'Doctor Booking', screens: [
            [vetScheduleVisit, 'Schedule Visit screen with available time slots'],
            [vetVisitReviews, 'Farm Visit Reviews listing past doctor visits'],
        ],
        title: 'Schedule Doctor Visit for Your Farms',
        points: [
            "Book a doctor's visit appointment for your Poultry Farms and get the right help for your flock's growth.",
            'Call our expert doctors for routine checkups and know the best products for your farm.',
        ],
    },
    {
        eyebrow: 'Support', screens: [
            [supportChat, 'Support screen for sending a message to the Poultryfy team'],
        ],
        title: 'Chat with Our Expert Team for Support',
        points: [
            'Raise tickets, create enquiries, or simply talk to our expert team.',
            'The support team will get back to you with the in-app chat feature.',
            'Share images, files and record chat until the issue is resolved.',
        ],
    },
]

/* One phone frame per feature; extra screenshots slide inside it. */
const AUTO_MS = 3600

// Advances on its own while the phone is on screen. It holds while the pointer
// or keyboard focus is on it, and stops for good once the viewer takes over
// with an arrow or a dot.
const useAutoAdvance = (ref, count, next) => {
    const [onScreen, setOnScreen] = useState(false)
    const [held, setHeld] = useState(false)
    const [takenOver, setTakenOver] = useState(false)

    useEffect(() => {
        if (count < 2) return undefined
        const observer = new IntersectionObserver(
            ([entry]) => setOnScreen(entry.isIntersecting),
            { threshold: 0.4 },
        )
        observer.observe(ref.current)
        return () => observer.disconnect()
    }, [ref, count])

    useEffect(() => {
        if (count < 2 || takenOver || held || !onScreen || prefersReducedMotion()) return undefined
        const id = setInterval(() => { if (!document.hidden) next() }, AUTO_MS)
        return () => clearInterval(id)
    }, [count, takenOver, held, onScreen, next])

    return {
        stop: () => setTakenOver(true),
        holdProps: {
            onMouseEnter: () => setHeld(true),
            onMouseLeave: () => setHeld(false),
            onFocus: () => setHeld(true),
            onBlur: () => setHeld(false),
        },
    }
}

const ScreenCarousel = ({ screens, label }) => {
    const [index, setIndex] = useState(0)
    const ref = useRef(null)
    const count = screens.length

    const next = useCallback(() => setIndex((i) => (i + 1) % count), [count])
    const { stop, holdProps } = useAutoAdvance(ref, count, next)

    const go = (step) => {
        stop()
        setIndex((i) => (i + step + count) % count)
    }
    const jumpTo = (i) => {
        stop()
        setIndex(i)
    }
    const onKeyDown = (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1) }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1) }
    }

    return (
        <div
            className="screens"
            ref={ref}
            role="group"
            aria-roledescription="carousel"
            aria-label={`${label} screenshots`}
            onKeyDown={count > 1 ? onKeyDown : undefined}
            {...(count > 1 ? holdProps : {})}
        >
            <div className="phone">
                <div className="phone-screen">
                    <div className="screen-track" style={{ transform: `translateX(-${index * 100}%)` }}>
                        {screens.map(([src, alt], i) => (
                            <img
                                key={src}
                                src={src}
                                alt={alt}
                                width="480"
                                height="1067"
                                loading="lazy"
                                decoding="async"
                                aria-hidden={i !== index}
                            />
                        ))}
                    </div>
                </div>
            </div>
            {count > 1 && (
                <>
                    <button type="button" className="screen-nav prev" onClick={() => go(-1)} aria-label="Previous screenshot">
                        <Icon name="chevronLeft" />
                    </button>
                    <button type="button" className="screen-nav next" onClick={() => go(1)} aria-label="Next screenshot">
                        <Icon name="chevronRight" />
                    </button>
                    <div className="screen-dots">
                        {screens.map(([src], i) => (
                            <button
                                key={src}
                                type="button"
                                className={i === index ? 'active' : ''}
                                aria-label={`Screenshot ${i + 1} of ${count}`}
                                aria-current={i === index}
                                onClick={() => jumpTo(i)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

export const Features = () => (
    <section className="section features">
        <div className="wrap">
            {FEATURES.map((f, i) => {
                const reverse = i % 2 === 1
                return (
                    <div className={`feature-row${reverse ? ' reverse' : ''}`} key={f.title}>
                        <div className={`feature-copy ${reverse ? 'reveal-right' : 'reveal-left'}`}>
                            <span className="eyebrow">{f.eyebrow}</span>
                            <h3>{f.title}</h3>
                            <ul className="feature-list">
                                {f.points.map((p) => (
                                    <li key={p}><span className="check"><Icon name="check" /></span>{p}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="feature-media reveal-zoom">
                            <ScreenCarousel screens={f.screens} label={f.eyebrow} />
                        </div>
                    </div>
                )
            })}
        </div>
    </section>
)

/* ---------- comparison table ---------- */

const YES = 'yes'
const NO = 'no'
const COMPARE = [
    ['Daily Rate Updates', NO, 'Manual', YES],
    ['Flock & Farm Records', 'Error-prone', NO, YES],
    ['Vet Access', NO, 'Slow', YES],
    ['Marketplace Access', NO, 'Limited', YES],
    ['Reports & Insights', NO, NO, YES],
    ['Reminders & Alerts', NO, NO, YES],
]

const CompareCell = ({ value }) => {
    if (value === YES) return <td className="yes"><Icon name="check" /><span className="sr-only"> Yes</span></td>
    if (value === NO) return <td className="no"><Icon name="x" /><span className="sr-only"> No</span></td>
    return <td className="no">{value}</td>
}

export const Compare = () => (
    <section className="section">
        <div className="wrap">
            <div className="head-block reveal">
                <span className="eyebrow">Why Poultryfy</span>
                <h2>Built for modern poultry farming, beyond notebooks and calls</h2>
            </div>
            <div className="compare-scroll">
                <table className="compare reveal-flip">
                    <thead>
                        <tr><th>Feature</th><th>Notebook / Memory</th><th>Calling Vendors</th><th>Poultryfy</th></tr>
                    </thead>
                    <tbody>
                        {COMPARE.map(([feature, ...cells]) => (
                            <tr key={feature}>
                                <td>{feature}</td>
                                {cells.map((c, i) => <CompareCell value={c} key={i} />)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </section>
)

/* ---------- testimonials (placeholder quotes from the design) ---------- */

const TESTIMONIALS = [
    { tag: 'Farm Management', quote: 'Poultryfy helped me track three farms without mixing up records. The flock reports save me hours every week.', initials: 'SK', name: 'Sandeep Kumar', role: 'Poultry Farmer, Haryana' },
    { tag: 'Live Rates', quote: 'I check the app every morning before deciding where to sell. The rate updates are accurate and always on time.', initials: 'RP', name: 'Ramesh Patil', role: 'Poultry Trader, Punjab' },
    { tag: 'Vet Support', quote: 'Booking a vet visit used to take days of calling around. Now I do it in the app in under a minute.', initials: 'AG', name: 'Anjali Gupta', role: 'Layer Farm Owner, UP' },
]

export const Testimonials = () => (
    <section className="section section-cream" id="testimonials">
        <div className="egg-bg a" /><div className="egg-bg b" />
        <div className="wrap">
            <div className="head-block center reveal">
                <span className="eyebrow">Our Farmers</span>
                <h2>Our clients know the value we provide</h2>
            </div>
            <div className="test-grid reveal-stagger">
                {TESTIMONIALS.map((t) => (
                    <div className="test-card tilt" key={t.name}>
                        <span className="test-tag">{t.tag}</span>
                        <p className="quote">“{t.quote}”</p>
                        <div className="test-person">
                            <div className="avatar">{t.initials}</div>
                            <div><div className="test-name">{t.name}</div><div className="test-role">{t.role}</div></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
)

/* ---------- FAQ ---------- */

const FAQS = [
    { q: 'What is Poultryfy?', a: "Poultryfy is India's poultry marketplace and farm management app, helping farmers track rates, manage farms and flocks, book vets, and trade poultry products." },
    { q: 'Is Poultryfy free to use?', a: 'Yes. The Poultryfy app is completely free of cost to download and use.' },
    {
        q: 'How do I book a vet visit?',
        a: 'In the Poultryfy app:',
        steps: [
            'Open the app and go to Book Visit.',
            'Select the farm and flock that needs the visit.',
            'Choose a date and time slot.',
            'Confirm your booking.',
        ],
    },
    { q: 'Which cities and states does Poultryfy cover?', a: 'Poultryfy is available pan India. You can use the app from any city or state in the country.' },
    {
        q: 'How do I list products on the marketplace?',
        a: 'The Poultryfy Marketplace is coming soon. Once it is live, vendors will be able to list, directly in the app:',
        bullets: ['Feed', 'Equipment', 'Eggs', 'Chicks'],
    },
]

export const Faq = () => {
    const [openIndex, setOpenIndex] = useState(0)

    return (
        <section className="section" id="faq">
            <div className="wrap faq-wrap">
                <div className="head-block reveal">
                    <span className="eyebrow">Frequently Asked Questions</span>
                    <h2>Still have questions? We've got answers</h2>
                </div>
                <div className="faq-list">
                    {FAQS.map((item, i) => {
                        const open = openIndex === i
                        return (
                            <div className={`faq-item${open ? ' open' : ''}`} key={item.q}>
                                <button
                                    type="button"
                                    className="faq-q"
                                    aria-expanded={open}
                                    aria-controls={`faq-a-${i}`}
                                    onClick={() => setOpenIndex(open ? -1 : i)}
                                >
                                    {item.q}<span className="faq-plus" aria-hidden="true">{open ? '−' : '+'}</span>
                                </button>
                                <div className="faq-a" id={`faq-a-${i}`} role="region">
                                    <div>
                                        <p>{item.a}</p>
                                        {item.steps && (
                                            <ol className="faq-steps">
                                                {item.steps.map((step) => <li key={step}>{step}</li>)}
                                            </ol>
                                        )}
                                        {item.bullets && (
                                            <ul className="faq-bullets">
                                                {item.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

/* ---------- contact ---------- */

const ContactDetails = () => (
    <>
        <div className="contact-detail">
            <div className="egg-icon"><Icon name="phone" /></div>
            <div><strong>Contact Us</strong><br /><a className="muted" href={CONTACT.phoneHref}>{CONTACT.phone}</a></div>
        </div>
        <div className="contact-detail">
            <div className="egg-icon"><Icon name="mapPin" /></div>
            <div><strong>Head Office</strong><br /><span className="muted">{CONTACT.address}</span></div>
        </div>
        <div className="contact-detail">
            <div className="egg-icon"><Icon name="mail" /></div>
            <div><strong>Email</strong><br /><a className="muted" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></div>
        </div>
    </>
)

export const ContactSection = () => (
    <section className="section section-cream" id="contact">
        <div className="egg-bg a" /><div className="egg-bg b" />
        <div className="wrap contact-wrap">
            <div className="contact-info reveal-left">
                <span className="eyebrow">Get in Touch</span>
                <h2>Feel free to contact us to learn more</h2>
                <p className="sub">Whether it's a question about your farm, a business enquiry, or feedback — our team is here to help.</p>
                <ContactDetails />
            </div>
            <div className="reveal-right">
                <ContactForm />
            </div>
        </div>
    </section>
)

/* ---------- download banner ---------- */

// Brand marks, inlined so there is no CDN request. Both are the official
// shapes, drawn at the sizes the stores allow.
const GooglePlayMark = () => (
    <svg className="store-mark" viewBox="0 0 512 512" aria-hidden="true">
        <path fill="#00A0FF" d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256z" />
        <path fill="#00E676" d="M325.3 234.3 104.6 13l280.8 161.2z" />
        <path fill="#FF3A44" d="M104.6 499l280.8-161.2-60.1-60.1z" />
        <path fill="#FFC900" d="m472.2 225.6-88.2-51.1-65.2 65.1 65.2 65.1 90-51.1c27-21.2 27-53.6-1.8-68z" />
    </svg>
)

const AppleMark = () => (
    <svg className="store-mark" viewBox="0 0 384 512" aria-hidden="true">
        <path fill="#fff" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
)

const STORE_BADGES = [
    { key: 'play', href: APP_LINKS.userPlayStore, lead: 'Get it on', name: 'Google Play', Mark: GooglePlayMark, aria: 'Download Poultryfy on Google Play' },
    { key: 'apple', href: APP_LINKS.userAppStore, lead: 'Download on the', name: 'App Store', Mark: AppleMark, aria: 'Download Poultryfy on the App Store' },
]

const DOWNLOAD_POINTS = [
    'Free to download — no subscription',
    'Live rates from across India, updated daily',
    'Works on Android and iPhone',
]

export const DownloadBanner = () => (
    <section className="section" id="download">
        <div className="wrap">
            <div className="download-banner reveal-zoom">
                <div className="download-copy">
                    <span className="eyebrow download-eyebrow">Get the App</span>
                    <h2>Download Poultryfy Now</h2>
                    <p>Track rates, manage farms and flocks, book vets and trade poultry products — all from your phone.</p>
                    <ul className="dl-points">
                        {DOWNLOAD_POINTS.map((point) => (
                            <li className="dl-point" key={point}>
                                <span className="tick"><Icon name="check" /></span>{point}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="store-badges">
                    {STORE_BADGES.map(({ key, href, lead, name, Mark, aria }) => (
                        <a className="store-badge" key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={aria}>
                            <Mark />
                            <span className="store-text"><span>{lead}</span><b>{name}</b></span>
                        </a>
                    ))}
                </div>
            </div>
        </div>
    </section>
)
